/**
 * Map definitions: geometry as **content**, not code.
 *
 * A map is a JSON file under `shared/maps/`. Both tiers parse it through this one
 * module, which is the point — the server's collision world and the client's
 * prediction world must be built from identical geometry, and the netcode GDD is
 * blunt about what happens otherwise: every tick corrects, there is no graceful
 * degradation, and it must be loud.
 *
 * Loudness is what `mapContentHash` buys. The server sends its map name and hash
 * in WELCOME; a client whose bundled map hashes differently knows immediately,
 * rather than discovering it as an unexplained correction storm. The hash covers
 * only what affects simulation — brushes and spawns — so retitling a map or
 * swapping its visual mesh does not invalidate a client that predicts correctly.
 *
 * Why JSON rather than a TypeScript constant: maps are authored by whoever makes
 * levels, shipped as assets, and eventually hot-swapped per room. Code is the
 * wrong container for content, and a compiled-in constant cannot be validated at
 * the boundary where bad data actually arrives.
 */
import { box, type Brush, type CollisionMap } from '../collision/brushes.js';
import { vec3, type Vec3 } from '../math.js';
import { TAU, wrapAngle } from '../quantize.js';
import { WORLD } from '../protocol.js';

/** Bumped only for breaking changes to the file layout; the parser rejects others. */
/**
 * The map a room runs, and the map a client predicts against, when nothing says otherwise.
 *
 * Declared here — in the one module both tiers compile — because the two defaults *must* be
 * the same value and previously were two independent literals. Diverging them does not fail
 * loudly: the client predicts against one geometry while the server simulates another, and
 * `describeMapMismatch` reports it only after a join has already happened. One constant
 * cannot disagree with itself.
 */
export const DEFAULT_MAP_NAME = 'blackline-kasbah';

export const MAP_FORMAT_VERSION = 1;

/** Where a player can appear, and which way they face on arrival. */
export interface SpawnPoint {
  /** Floor position. The capsule centre is placed height/2 above this. */
  position: Vec3;
  /** Facing on spawn, radians, normalized into [0, τ). */
  yaw: number;
  /**
   * The team this spawn belongs to, or `null` for a spawn anyone may use.
   *
   * Null on every map written before the competitive mode, and still the right answer for
   * deathmatch, where the whole point is that spawns are shared. A round-based mode needs the
   * two sides to start apart — a CT spawning into the T spawn is not a round, it is a
   * coin flip — so it refuses a map whose spawns are all unowned rather than seating both
   * sides in the same corner.
   */
  team: 0 | 1 | null;
}

/**
 * A bombsite: the volume an attacker may plant in.
 *
 * Content, not code, for exactly the reason maps are (`CLAUDE.md`) — a site is a decision
 * about a specific map's layout, and putting it in a TypeScript table would mean a level
 * designer cannot move it. An axis-aligned box rather than a polygon because that is what
 * every brush in this format already is, and a plant test is then a three-axis comparison
 * rather than a point-in-polygon routine on the hot path.
 */
export interface BombSite {
  /** Short label shown on the HUD and in the kill feed: conventionally `A` and `B`. */
  id: string;
  /** Centre of the plantable volume. */
  center: Vec3;
  /** Half-size on each axis. The Y extent matters: a site is not plantable from a roof. */
  halfExtents: Vec3;
}

/**
 * Optional visual asset. Collision never comes from it — see `brushes.ts` for why
 * the two are separate — so a map with `visual: null` renders its brushes instead,
 * which is exactly the greybox look P0 shipped.
 */
export interface MapVisual {
  /** Path to a glTF/GLB, relative to the client's asset root. */
  gltf: string;
}

/**
 * The closed set of surface looks a brush may declare. Closed rather than a free-form
 * texture path for the same reason `visual.gltf` is *not* how this works: a map file is
 * untrusted content, and "name any file you like" is how a map ends up pointing at
 * something that was never vetted to ship. A short enum is also just enough — surfaces
 * are read for material *type*, not for a specific texture asset.
 */
export const SURFACE_MATERIALS = [
  'concrete',
  'metal',
  'brick',
  'sand',
  'wood',
  'asphalt',
  // The second vocabulary (2026-09-08, the map rebuild): each of these is a real CC0 PBR set
  // under `frontend/public/game/textures/pbr/<name>/` (albedo + normal + AO/rough/metal),
  // fetched and recorded by `tools/maps/fetch-textures.mjs`. The renderer keeps the six above
  // on the same footing — every name here has exactly one texture set behind it.
  'plaster',
  'rust',
  'panel',
  'gravel',
  'tile',
  'paint',
] as const;
export type SurfaceMaterial = (typeof SURFACE_MATERIALS)[number];

/**
 * A collision brush plus the one cosmetic fact about it that the shape itself cannot
 * carry: what it looks like. Lives here, in the format module, rather than on `Brush`
 * in `brushes.ts` — that file is deliberately unaware that rendering exists at all, and
 * stays that way. `material` is never read by `toCollisionMap`'s consumers (the physics
 * world, movement, hitscan); it rides along on the same object purely so the client's
 * renderer does not need a second array to stay in sync with the first.
 *
 * Optional and defaulted rather than required: every map that predates this field —
 * `greybox-01`, `blackline-depot` — must go on validating unchanged, rendered by the
 * shape-based fallback `scene.ts` already had before this existed.
 */
export interface MapBrush extends Brush {
  material?: SurfaceMaterial;
  /**
   * Self-illumination, as `#rrggbb`, with an optional intensity (default 1). Cosmetic and
   * un-hashed like `material`. This is how a map draws a light FIXTURE — a strip lamp, a lit
   * sign, a furnace mouth: the brush glows, and a `lights[]` entry beside it does the actual
   * illuminating. The renderer never derives a light from an emissive brush, so a glow that
   * lights nothing is a map-author decision and not a renderer surprise.
   */
  emissive?: string;
  emissiveIntensity?: number;
  /**
   * Collision-only: the brush stops players and bullets but is never drawn.
   *
   * Exists for Synty-dressed maps, where a placed model (a container, a warehouse) is the
   * visual and the brush is its physics. Before this field the renderer drew a grey box
   * *inside* every dressed prop — invisible geometry was never the risk; doubled geometry was.
   *
   * The old invariant "everything that collides is visible" survives in the only form that
   * ever mattered to a player: every hidden brush must sit inside dressing that shows where
   * the wall is. `map:check` enforces the pairing (a hidden brush with no prop overlapping it
   * fails the map), so a bare invisible wall cannot ship by accident.
   *
   * Cosmetic classification, like `material`: excluded from `mapContentHash` because both
   * sides simulate the brush identically whether or not one of them draws it.
   */
  hidden?: boolean;
}

/**
 * Cosmetic-only dressing: a model placed in the world with no collision of its own.
 *
 * If a prop needs to stop a player, a bullet, or anything else, that is a brush authored
 * alongside it — a prop can never grow collision by editing this file, because this type has
 * nowhere to put it. `model` is a path rather than a closed enum like `SurfaceMaterial`: props
 * already name one of 33 vendored files the same way `visual.gltf` names one file, and
 * `parseProps` still requires the `/game/models/` prefix so a map cannot point outside the
 * asset root that was actually vetted to ship.
 */
export interface MapProp {
  model: string;
  position: Vec3;
  /** Yaw only, radians, normalized into `[0, τ)`. Dressing sits on the ground; it does not
   * need pitch or roll to look like it belongs there. Defaults to 0. */
  yaw?: number;
  /** Uniform scale relative to the model's authored size. Defaults to 1. */
  scale?: number;
  /**
   * Yaw-resolved half-extents `[hx, hz]` of the model's footprint, metres — set by the map
   * generators for BUILDING props whose collision is a wall shell rather than one box.
   * `map:check`'s dressing gate needs it: a shell's wall/lintel brushes sit metres from the
   * prop's origin but INSIDE its visible mesh, and the origin-distance proxy cannot know
   * that without the footprint. Not hashed (the content hash covers brushes and spawns);
   * purely a validation aid, never collision.
   */
  footprint?: [number, number];
  /** Ignored by the loader; keeps a hundred-prop file readable. */
  note?: string;
}

/**
 * A light the map places: a point (bare bulb, fire) or a spot (a lamp with a hood, a
 * floodlight). Presentation only — NOT hashed, never read by the server, exactly like `props`
 * — so a lighting pass on a map does not turn into a correction storm for every client.
 *
 * `color` is `#rrggbb`. `intensity` is in three.js physical units (candela), `distance` the
 * cut-off in metres (0 = none, expensive), `decay` the falloff exponent (2 is physical). A
 * spot points from `position` at `target`, with `angle` its half-cone in radians and
 * `penumbra` the 0..1 soft edge. `castShadow` is a request the renderer may cap — every
 * shadow-casting light is six (point) or one (spot) extra depth passes per frame.
 */
export interface MapLight {
  kind: 'point' | 'spot';
  position: Vec3;
  color: string;
  intensity: number;
  distance: number;
  decay?: number;
  castShadow?: boolean;
  target?: Vec3;
  angle?: number;
  penumbra?: number;
  /** Ignored by the loader; keeps a lit map readable. */
  note?: string;
}

export interface MapDefinition {
  formatVersion: number;
  /** Stable id, kebab-case, and the filename stem. Used on the wire. */
  name: string;
  displayName: string;
  description: string;
  visual: MapVisual | null;
  brushes: MapBrush[];
  spawns: SpawnPoint[];
  /**
   * Visual-only dressing. Never touches `toCollisionMap` or `mapContentHash` — same reasoning
   * as `visual` and `MapBrush.material` — so redressing a map's clutter does not turn into a
   * correction storm for every connected client.
   */
  props: MapProp[];
  /**
   * Placed lights, or an empty array (every map before the 2026-09-08 rebuild). Cosmetic, like
   * `props` — see `MapLight`.
   */
  lights: MapLight[];
  /**
   * Bombsites, or an empty array on a map that has none.
   *
   * Empty is the normal state: seven of the eight shipped maps were built for deathmatch and
   * have no objective at all. A competitive room refuses a map with no sites rather than
   * falling back to a siteless round — the same rule `pinnedRotationMap()` applies to an
   * unpinned rotation, and for the same reason: a mode quietly running rules nobody asked
   * for is worse than a room that will not start.
   */
  sites: BombSite[];
  /**
   * Lighting hint: the name of a client environment preset (`render-settings.ts`) this map
   * was graded for. Presentation only — NOT hashed, never read by collision or the server —
   * exactly like `material` and `footprint`. **Since 2026-09-11 the arena ignores it**: every
   * match runs `ARENA_ENVIRONMENT` (night). Only the editor still reads it, as the sky to
   * open a map under. `null`/absent means "no opinion". Kept a bounded token, not a free path,
   * for the same reason `material` is a closed enum: map files are untrusted content.
   */
  environment: string | null;
}

/** A problem found in a map file. `path` points at the offending field. */
export interface MapProblem {
  path: string;
  message: string;
}

export class MapParseError extends Error {
  constructor(
    readonly source: string,
    readonly problems: MapProblem[],
  ) {
    super(
      `invalid map "${source}":\n${problems.map((p) => `  ${p.path}: ${p.message}`).join('\n')}`,
    );
    this.name = 'MapParseError';
  }
}

/**
 * Parses and validates a raw map object.
 *
 * Every field is checked, and all problems are collected before throwing rather
 * than failing on the first: a map author fixing one typo per run is a waste of
 * their afternoon. `source` only appears in error messages.
 */
export function parseMapDefinition(raw: unknown, source = '<inline>'): MapDefinition {
  const problems: MapProblem[] = [];

  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new MapParseError(source, [{ path: '.', message: 'expected a JSON object' }]);
  }
  const input = raw as Record<string, unknown>;

  if (input.formatVersion !== MAP_FORMAT_VERSION) {
    problems.push({
      path: 'formatVersion',
      message: `expected ${MAP_FORMAT_VERSION}, got ${JSON.stringify(input.formatVersion)}`,
    });
  }

  const name = requireMapName(input.name, problems);
  const displayName = typeof input.displayName === 'string' ? input.displayName : name;
  const description = typeof input.description === 'string' ? input.description : '';
  const visual = parseVisual(input.visual, problems);
  const brushes = parseBrushes(input.brushes, problems);
  const spawns = parseSpawns(input.spawns, problems);
  const props = parseProps(input.props, problems);
  const lights = parseLights(input.lights, problems);
  const sites = parseSites(input.sites, problems);
  let environment: string | null = null;
  if (input.environment !== undefined && input.environment !== null) {
    if (typeof input.environment === 'string' && /^[a-z][a-z0-9-]{0,31}$/.test(input.environment)) {
      environment = input.environment;
    } else {
      problems.push({
        path: 'environment',
        message: 'expected a lowercase preset token (max 32 chars) or null',
      });
    }
  }

  if (problems.length > 0) throw new MapParseError(source, problems);

  return {
    formatVersion: MAP_FORMAT_VERSION,
    name,
    displayName,
    description,
    visual,
    brushes,
    spawns,
    props,
    lights,
    sites,
    environment,
  };
}

/** The collision view of a map, which is all `CollisionWorld` needs. */
export function toCollisionMap(definition: MapDefinition): CollisionMap {
  return {
    name: definition.name,
    brushes: definition.brushes,
    spawns: definition.spawns.map((spawn) => spawn.position),
  };
}

/**
 * 32-bit FNV-1a over a canonical rendering of everything that affects simulation.
 *
 * Hand-rolled on purpose. `crypto.subtle` is async and differs between Node and
 * the browser, and a hash whose implementation differs per platform cannot do the
 * one job this hash exists for. FNV-1a is not cryptographic and does not need to
 * be: this detects a stale asset, not a forged one — and the server is
 * authoritative regardless of what the client claims to be running.
 *
 * Coordinates are rendered at 4 decimal places, which is finer than the wire
 * quantization (3.9mm) and coarser than float noise, so a map that round-trips
 * through a JSON writer hashes the same.
 */
export function mapContentHash(definition: MapDefinition): number {
  const parts: string[] = [`v${definition.formatVersion}`, definition.name];

  for (const brush of definition.brushes) {
    // `brush.material` is deliberately not read here — same reasoning as `visual` never
    // appearing in this function at all. It is cosmetic, the server never loads a texture,
    // and a map author repainting a wall must not force every client to reconnect.
    parts.push(
      [brush.kind, fixed(brush.center), fixed(brush.halfExtents)].join(':'),
    );
  }
  for (const spawn of definition.spawns) {
    // The team is hashed: it decides where a side starts, so a client holding a version of
    // the map where it differs is holding a different game, not a different picture.
    parts.push(['spawn', fixed(spawn.position), spawn.yaw.toFixed(4), String(spawn.team)].join(':'));
  }
  for (const site of definition.sites) {
    // Hashed for the same reason brushes are: the site volume decides whether a plant is
    // allowed, and a client drawing a prompt where the server refuses one is the exact
    // disagreement this hash exists to catch.
    parts.push(['site', site.id, fixed(site.center), fixed(site.halfExtents)].join(':'));
  }

  return fnv1a(parts.join('|'));
}

/** Hex form of `mapContentHash`, for logs and error messages. */
export function formatMapHash(hash: number): string {
  return hash.toString(16).padStart(8, '0');
}

// ─── Geometry checks (pure; the physics-based ones live in map:check) ─────────

/**
 * Structural problems a map file can have that the schema cannot express.
 *
 * Deliberately not part of `parseMapDefinition`: a map that is *loadable* should
 * load, so the client can render something and the error can be reported once,
 * where it is actionable. `yarn map:check` treats every problem here as a failure.
 */
export function validateMapGeometry(definition: MapDefinition): MapProblem[] {
  const problems: MapProblem[] = [];

  if (definition.brushes.length === 0) {
    problems.push({ path: 'brushes', message: 'a map needs at least one brush to stand on' });
  }
  if (definition.spawns.length === 0) {
    problems.push({ path: 'spawns', message: 'a map needs at least one spawn point' });
  }

  definition.brushes.forEach((brush, i) => {
    const min = {
      x: brush.center.x - brush.halfExtents.x,
      y: brush.center.y - brush.halfExtents.y,
      z: brush.center.z - brush.halfExtents.z,
    };
    const max = {
      x: brush.center.x + brush.halfExtents.x,
      y: brush.center.y + brush.halfExtents.y,
      z: brush.center.z + brush.halfExtents.z,
    };
    // Geometry outside the quantization bounds cannot be described on the wire, so
    // a player standing there would have their position clamped — they would appear
    // somewhere else to everyone including themselves.
    if (min.x < WORLD.minXZ || max.x > WORLD.maxXZ || min.z < WORLD.minXZ || max.z > WORLD.maxXZ) {
      problems.push({
        path: `brushes[${i}]`,
        message: `extends outside the wire's horizontal bounds (${WORLD.minXZ}..${WORLD.maxXZ}m)`,
      });
    }
    if (min.y < WORLD.minY || max.y > WORLD.maxY) {
      problems.push({
        path: `brushes[${i}]`,
        message: `extends outside the wire's vertical bounds (${WORLD.minY}..${WORLD.maxY}m)`,
      });
    }
  });

  for (const problem of coplanarTopOverlaps(definition.brushes)) problems.push(problem);

  definition.spawns.forEach((spawn, i) => {
    if (
      spawn.position.x < WORLD.minXZ ||
      spawn.position.x > WORLD.maxXZ ||
      spawn.position.z < WORLD.minXZ ||
      spawn.position.z > WORLD.maxXZ ||
      spawn.position.y < WORLD.minY ||
      spawn.position.y > WORLD.maxY
    ) {
      problems.push({ path: `spawns[${i}]`, message: 'outside the wire bounds' });
    }

    // A spawn inside solid geometry puts the player's capsule in a brush, where the
    // controller's job is to push it out — usually straight up through the ceiling.
    const inside = definition.brushes.findIndex((brush) => containsPoint(brush, spawn.position));
    if (inside >= 0) {
      problems.push({ path: `spawns[${i}]`, message: `inside brushes[${inside}]` });
    }
  });

  return problems;
}

/** Two tops in the same plane are "the same plane" within this, in metres. */
const COPLANAR_EPSILON = 1e-6;
/** Footprints that share only an edge (a butt joint) overlap by at most this, in metres. */
const TOUCH_EPSILON = 1e-4;

/**
 * Pairs of drawn brushes whose TOP faces lie in one plane and whose footprints overlap.
 *
 * This is z-fighting, and it is a map-content bug rather than a renderer one: two coplanar
 * faces in the same place give the depth buffer no answer, so the surface flickers between
 * the two materials as the camera moves. It shipped twice — 135 pairs in `blackline-kasbah`
 * and 26 in `blackline-foundry` on 2026-09-08, including the base floor under the plaza the
 * player looks at most, and every corner of every `shell()` — which is why it is a hard
 * failure here instead of a note: `map:check` gates on this list, and `writeMap` refuses to
 * write a generator's output that has one.
 *
 * A material patch laid on a floor must sit ABOVE it (`pad()` in `tools/maps/lib.ts` lifts
 * by 1cm — invisible to the player, decisive for the depth buffer, and far under the
 * controller's step height) and abutting brushes must butt rather than overlap: touching
 * exactly is fine, which is what `TOUCH_EPSILON` allows for.
 *
 * `hidden` brushes are exempt: they are collision that is never drawn, so two of them, or one
 * of them and a drawn brush, cannot fight over a pixel.
 *
 * Bucketed by top height so this stays linear in practice — a 700-brush map has ~40 distinct
 * top planes, and only brushes sharing one are ever compared.
 */
function coplanarTopOverlaps(brushes: readonly MapBrush[]): MapProblem[] {
  const problems: MapProblem[] = [];
  const byTop = new Map<number, number[]>();
  brushes.forEach((brush, i) => {
    if (brush.hidden) return;
    const key = Math.round((brush.center.y + brush.halfExtents.y) / COPLANAR_EPSILON);
    const bucket = byTop.get(key);
    if (bucket) bucket.push(i);
    else byTop.set(key, [i]);
  });
  const overlap = (ca: number, ha: number, cb: number, hb: number): number =>
    Math.min(ca + ha, cb + hb) - Math.max(ca - ha, cb - hb);
  for (const bucket of byTop.values()) {
    for (let a = 0; a < bucket.length; a++) {
      for (let b = a + 1; b < bucket.length; b++) {
        const i = bucket[a] as number;
        const j = bucket[b] as number;
        const one = brushes[i] as MapBrush;
        const two = brushes[j] as MapBrush;
        const ox = overlap(one.center.x, one.halfExtents.x, two.center.x, two.halfExtents.x);
        const oz = overlap(one.center.z, one.halfExtents.z, two.center.z, two.halfExtents.z);
        if (ox <= TOUCH_EPSILON || oz <= TOUCH_EPSILON) continue;
        const top = one.center.y + one.halfExtents.y;
        problems.push({
          path: `brushes[${i}]`,
          message:
            `top face at y=${top.toFixed(3)} is coplanar with brushes[${j}] and their footprints overlap ` +
            `${ox.toFixed(3)}x${oz.toFixed(3)}m — z-fighting (${one.material ?? 'no material'} vs ` +
            `${two.material ?? 'no material'}). ` +
            'Lift the upper brush (pad(), +0.01m) or butt them instead of overlapping.',
        });
      }
    }
  }
  return problems;
}

function containsPoint(brush: Brush, point: Vec3): boolean {
  return (
    Math.abs(point.x - brush.center.x) < brush.halfExtents.x &&
    Math.abs(point.y - brush.center.y) < brush.halfExtents.y &&
    Math.abs(point.z - brush.center.z) < brush.halfExtents.z
  );
}

// ─── Parsing helpers ────────────────────────────────────────────────────────

function requireMapName(value: unknown, problems: MapProblem[]): string {
  if (typeof value !== 'string' || !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(value)) {
    problems.push({
      path: 'name',
      message: 'expected a kebab-case id matching the filename, e.g. "greybox-01"',
    });
    return 'invalid';
  }
  // Sent on the wire in WELCOME, where the length is a single byte.
  if (value.length > MAX_MAP_NAME_BYTES) {
    problems.push({ path: 'name', message: `longer than ${MAX_MAP_NAME_BYTES} characters` });
  }
  return value;
}

/** Cap on the wire form of a map name (WELCOME carries a 1-byte length). */
export const MAX_MAP_NAME_BYTES = 64;

function parseVisual(value: unknown, problems: MapProblem[]): MapVisual | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'object' || Array.isArray(value)) {
    problems.push({ path: 'visual', message: 'expected null or { gltf: string }' });
    return null;
  }
  const gltf = (value as Record<string, unknown>).gltf;
  if (typeof gltf !== 'string' || gltf.length === 0) {
    problems.push({ path: 'visual.gltf', message: 'expected a non-empty path' });
    return null;
  }
  return { gltf };
}

function parseBrushes(value: unknown, problems: MapProblem[]): MapBrush[] {
  if (!Array.isArray(value)) {
    problems.push({ path: 'brushes', message: 'expected an array' });
    return [];
  }

  const brushes: MapBrush[] = [];
  value.forEach((entry, i) => {
    if (typeof entry !== 'object' || entry === null) {
      problems.push({ path: `brushes[${i}]`, message: 'expected an object' });
      return;
    }
    const brush = entry as Record<string, unknown>;
    if (brush.kind !== 'box') {
      problems.push({
        path: `brushes[${i}].kind`,
        message: `expected "box" (the only primitive so far), got ${JSON.stringify(brush.kind)}`,
      });
      return;
    }

    const center = parseVec3(brush.center, `brushes[${i}].center`, problems);
    const halfExtents = parseVec3(brush.halfExtents, `brushes[${i}].halfExtents`, problems);
    if (!center || !halfExtents) return;

    if (halfExtents.x <= 0 || halfExtents.y <= 0 || halfExtents.z <= 0) {
      problems.push({
        path: `brushes[${i}].halfExtents`,
        message: 'must be positive on every axis (these are half-sizes, not corners)',
      });
      return;
    }

    const material = parseMaterial(brush.material, `brushes[${i}].material`, problems);
    if (brush.hidden !== undefined && typeof brush.hidden !== 'boolean') {
      problems.push({ path: `brushes[${i}].hidden`, message: 'expected true or false (or omit)' });
      return;
    }
    const parsed: MapBrush = box(center, halfExtents);
    if (material) parsed.material = material;
    if (brush.hidden === true) parsed.hidden = true;
    if (brush.emissive !== undefined) {
      if (typeof brush.emissive === 'string' && HEX_COLOUR.test(brush.emissive)) {
        parsed.emissive = brush.emissive.toLowerCase();
      } else {
        problems.push({ path: `brushes[${i}].emissive`, message: 'expected "#rrggbb" (or omit)' });
        return;
      }
      if (brush.emissiveIntensity !== undefined) {
        if (typeof brush.emissiveIntensity === 'number' && Number.isFinite(brush.emissiveIntensity) && brush.emissiveIntensity >= 0) {
          parsed.emissiveIntensity = brush.emissiveIntensity;
        } else {
          problems.push({ path: `brushes[${i}].emissiveIntensity`, message: 'expected a non-negative number (or omit)' });
          return;
        }
      }
    }
    brushes.push(parsed);
  });

  return brushes;
}

/**
 * `undefined` renders the shape-based fallback `scene.ts` used before this field existed —
 * not a validation failure, so every map that predates materials keeps loading unchanged.
 * An unrecognised string, on the other hand, is rejected outright: that is the enforcement
 * of the closed vocabulary, and a typo (`"concrette"`) is exactly the case it exists to catch.
 */
function parseMaterial(
  value: unknown,
  path: string,
  problems: MapProblem[],
): SurfaceMaterial | undefined {
  if (value === undefined) return undefined;
  if (typeof value === 'string' && (SURFACE_MATERIALS as readonly string[]).includes(value)) {
    return value as SurfaceMaterial;
  }
  problems.push({
    path,
    message: `expected one of ${SURFACE_MATERIALS.join(', ')} (or omit it), got ${JSON.stringify(value)}`,
  });
  return undefined;
}

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

/** Every problem reported, none fatal to the rest of the file — the same rule as brushes. */
function parseLights(value: unknown, problems: MapProblem[]): MapLight[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) {
    problems.push({ path: 'lights', message: 'expected an array (or omit)' });
    return [];
  }
  const lights: MapLight[] = [];
  value.forEach((entry, i) => {
    const path = `lights[${i}]`;
    if (typeof entry !== 'object' || entry === null) {
      problems.push({ path, message: 'expected an object' });
      return;
    }
    const light = entry as Record<string, unknown>;
    if (light.kind !== 'point' && light.kind !== 'spot') {
      problems.push({ path: `${path}.kind`, message: `expected "point" or "spot", got ${JSON.stringify(light.kind)}` });
      return;
    }
    const position = parseVec3(light.position, `${path}.position`, problems);
    if (!position) return;
    if (typeof light.color !== 'string' || !HEX_COLOUR.test(light.color)) {
      problems.push({ path: `${path}.color`, message: 'expected "#rrggbb"' });
      return;
    }
    const number = (key: string, fallback: number | undefined, min: number): number | undefined => {
      const raw = light[key];
      if (raw === undefined) return fallback;
      if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < min) {
        problems.push({ path: `${path}.${key}`, message: `expected a finite number >= ${min}` });
        return undefined;
      }
      return raw;
    };
    const intensity = number('intensity', undefined, 0);
    const distance = number('distance', 0, 0);
    if (intensity === undefined || distance === undefined) {
      if (light.intensity === undefined) problems.push({ path: `${path}.intensity`, message: 'required' });
      return;
    }
    const parsed: MapLight = { kind: light.kind, position, color: light.color.toLowerCase(), intensity, distance };
    const decay = number('decay', undefined, 0);
    if (decay !== undefined) parsed.decay = decay;
    if (light.castShadow !== undefined) {
      if (typeof light.castShadow !== 'boolean') {
        problems.push({ path: `${path}.castShadow`, message: 'expected true or false (or omit)' });
        return;
      }
      if (light.castShadow) parsed.castShadow = true;
    }
    if (light.kind === 'spot') {
      const target = parseVec3(light.target ?? [position.x, 0, position.z], `${path}.target`, problems);
      if (!target) return;
      parsed.target = target;
      const angle = number('angle', Math.PI / 6, 0);
      const penumbra = number('penumbra', 0.3, 0);
      if (angle === undefined || penumbra === undefined) return;
      if (angle > Math.PI / 2) {
        problems.push({ path: `${path}.angle`, message: 'a spot cone is at most π/2 radians' });
        return;
      }
      parsed.angle = angle;
      parsed.penumbra = Math.min(1, penumbra);
    }
    lights.push(parsed);
  });
  return lights;
}

function parseSpawns(value: unknown, problems: MapProblem[]): SpawnPoint[] {
  if (!Array.isArray(value)) {
    problems.push({ path: 'spawns', message: 'expected an array' });
    return [];
  }

  const spawns: SpawnPoint[] = [];
  value.forEach((entry, i) => {
    if (typeof entry !== 'object' || entry === null) {
      problems.push({ path: `spawns[${i}]`, message: 'expected an object' });
      return;
    }
    const spawn = entry as Record<string, unknown>;
    const position = parseVec3(spawn.position, `spawns[${i}].position`, problems);
    if (!position) return;

    const rawYaw = spawn.yaw ?? 0;
    if (typeof rawYaw !== 'number' || !Number.isFinite(rawYaw)) {
      problems.push({ path: `spawns[${i}].yaw`, message: 'expected a finite number of radians' });
      return;
    }

    // Absent means "anyone" — every map written before sides existed says nothing here, and
    // must keep loading unchanged. Anything present but not 0 or 1 is a typo worth reporting:
    // a spawn silently demoted to "anyone" would seat a CT in the T spawn once a round.
    let team: 0 | 1 | null = null;
    if (spawn.team !== undefined && spawn.team !== null) {
      if (spawn.team === 0 || spawn.team === 1) {
        team = spawn.team;
      } else {
        problems.push({ path: `spawns[${i}].team`, message: 'expected 0, 1, or null' });
        return;
      }
    }

    spawns.push({ position, yaw: wrapAngle(rawYaw) % TAU, team });
  });

  return spawns;
}

/**
 * Bombsites. Absent entirely is the common case — most maps have no objective.
 *
 * Ids are constrained to one or two uppercase letters, not free text: the id is drawn on a
 * HUD the size of a postage stamp and read aloud by players ("planted at A"), and a map that
 * called its sites "north courtyard" would break both.
 */
function parseSites(value: unknown, problems: MapProblem[]): BombSite[] {
  if (value === undefined) return [];
  if (!Array.isArray(value)) {
    problems.push({ path: 'sites', message: 'expected an array (or omit it entirely)' });
    return [];
  }

  const sites: BombSite[] = [];
  value.forEach((entry, i) => {
    if (typeof entry !== 'object' || entry === null) {
      problems.push({ path: `sites[${i}]`, message: 'expected an object' });
      return;
    }
    const site = entry as Record<string, unknown>;
    if (typeof site.id !== 'string' || !/^[A-Z]{1,2}$/.test(site.id)) {
      problems.push({ path: `sites[${i}].id`, message: 'expected one or two uppercase letters' });
      return;
    }
    if (sites.some((existing) => existing.id === site.id)) {
      problems.push({ path: `sites[${i}].id`, message: `duplicate site id "${site.id}"` });
      return;
    }
    const center = parseVec3(site.center, `sites[${i}].center`, problems);
    const halfExtents = parseVec3(site.halfExtents, `sites[${i}].halfExtents`, problems);
    if (!center || !halfExtents) return;
    if (halfExtents.x <= 0 || halfExtents.y <= 0 || halfExtents.z <= 0) {
      problems.push({ path: `sites[${i}].halfExtents`, message: 'every extent must be positive' });
      return;
    }
    sites.push({ id: site.id, center, halfExtents });
  });

  return sites;
}

/** Every prop must live under here — the one directory of vendored, licence-checked models
 * (`frontend/public/game/ASSETS.md`). Rejecting anything else is cheap insurance against a
 * typo'd or invented path silently rendering nothing (Vite's static server 200s a missing
 * path as HTML, so a wrong prefix would otherwise fail without a word from the parser). */
const PROP_MODEL_ROOT = '/game/models/';

/** Absent entirely, not just empty, is the common case: most maps predate props and every one
 * of them must go on validating with `props: []`. */
function parseProps(value: unknown, problems: MapProblem[]): MapProp[] {
  if (value === undefined) return [];
  if (!Array.isArray(value)) {
    problems.push({ path: 'props', message: 'expected an array (or omit it entirely)' });
    return [];
  }

  const props: MapProp[] = [];
  value.forEach((entry, i) => {
    if (typeof entry !== 'object' || entry === null) {
      problems.push({ path: `props[${i}]`, message: 'expected an object' });
      return;
    }
    const prop = entry as Record<string, unknown>;

    const model = prop.model;
    if (typeof model !== 'string' || !model.startsWith(PROP_MODEL_ROOT)) {
      problems.push({
        path: `props[${i}].model`,
        message: `expected a path under "${PROP_MODEL_ROOT}", got ${JSON.stringify(model)}`,
      });
      return;
    }

    const position = parseVec3(prop.position, `props[${i}].position`, problems);
    if (!position) return;

    const rawYaw = prop.yaw ?? 0;
    if (typeof rawYaw !== 'number' || !Number.isFinite(rawYaw)) {
      problems.push({ path: `props[${i}].yaw`, message: 'expected a finite number of radians' });
      return;
    }

    const rawScale = prop.scale ?? 1;
    if (typeof rawScale !== 'number' || !Number.isFinite(rawScale) || rawScale <= 0) {
      problems.push({ path: `props[${i}].scale`, message: 'expected a positive finite number' });
      return;
    }

    let footprint: [number, number] | undefined;
    if (prop.footprint !== undefined) {
      const fp = prop.footprint;
      const ok =
        Array.isArray(fp) &&
        fp.length === 2 &&
        fp.every((v) => typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= 64);
      if (!ok) {
        problems.push({
          path: `props[${i}].footprint`,
          message: 'expected [hx, hz], two positive finite metres <= 64',
        });
        return;
      }
      footprint = [fp[0], fp[1]];
    }

    props.push({
      model,
      position,
      yaw: wrapAngle(rawYaw) % TAU,
      scale: rawScale,
      ...(footprint ? { footprint } : {}),
    });
  });

  return props;
}

/** Accepts `[x, y, z]` — terser than `{x,y,z}` in a file with hundreds of brushes. */
function parseVec3(value: unknown, path: string, problems: MapProblem[]): Vec3 | null {
  if (!Array.isArray(value) || value.length !== 3) {
    problems.push({ path, message: 'expected [x, y, z]' });
    return null;
  }
  const [x, y, z] = value;
  if (
    typeof x !== 'number' ||
    typeof y !== 'number' ||
    typeof z !== 'number' ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(z)
  ) {
    problems.push({ path, message: 'expected three finite numbers' });
    return null;
  }
  return vec3(x, y, z);
}

function fixed(v: Vec3): string {
  return `${v.x.toFixed(4)},${v.y.toFixed(4)},${v.z.toFixed(4)}`;
}

function fnv1a(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    // >>> 0 after each multiply: JS numbers are doubles, and without the coercion
    // the value leaves the 32-bit range and the result stops being FNV-1a.
    hash = (hash * 0x01000193) >>> 0;
  }
  return hash >>> 0;
}
